import React, { Children } from 'react';

import {
  TFlexWrap,
  TJustify,
  TDirection,
  TAlignItems,
  styled,
  isPropValid,
  flex,
} from '@unfold/theme';
import { isNumber } from '@unfold/utils';

import { IGridProps } from './types';

const Container = styled('div', {
  label: 'grid-container',
})<IGridProps>`
  ${flex}
  width: 100%;
  max-width: ${({ maxWidth }): string | undefined =>
    maxWidth ? `${maxWidth}px` : undefined};
`;

const StyledWrapper = styled('div', {
  label: 'grid-wrapper',
  shouldForwardProp: (prop) => isPropValid(prop),
})<IGridProps>`
  ${flex}
  flex-wrap: ${({ noWrap = true }): TFlexWrap => (noWrap ? 'nowrap' : 'wrap')};
  justify-content: ${({ justify }): TJustify => justify};
  flex-direction: ${({ itemDirection }): TDirection => itemDirection};
  align-items: ${({ alignItems }): TAlignItems => alignItems};
  min-height: ${({ minHeight }): string =>
    isNumber(minHeight) ? `${minHeight}px` : 'auto'};
  margin: ${({ verticalPadding = 0 }): number => -verticalPadding / 2}px
    ${({ horizontalPadding = 0 }): number => -horizontalPadding / 2}px;
  width: calc(
    100% + ${({ horizontalPadding = 0 }): number => horizontalPadding}px
  );
`;
const Wrapper: React.FC<IGridProps> = (props) => <StyledWrapper {...props} />;

export const Grid = React.forwardRef<HTMLDivElement, IGridProps>(
  ({ maxWidth = undefined, children, onClick, ...wrapperProps }, ref) => {
    const {
      className,
      role,
      itemDirection,
      ...restOfWrapperProps
    } = wrapperProps;
    const hasGridRole = !role || role === 'grid';
    const hasCells = itemDirection === 'row';

    return (
      <Container
        ref={ref}
        maxWidth={maxWidth}
        className={className}
        role={hasGridRole ? 'grid' : role}
        onClick={onClick}
      >
        <Wrapper
          {...restOfWrapperProps}
          itemDirection={itemDirection}
          role={hasGridRole ? (hasCells ? 'row' : 'rowgroup') : undefined}
        >
          {Children.map(
            children,
            (child) =>
              child &&
              React.isValidElement(child) &&
              React.cloneElement(child, {
                horizontalPadding: wrapperProps.horizontalPadding,
                verticalPadding: wrapperProps.verticalPadding,
                role: hasGridRole ? (hasCells ? 'gridcell' : 'row') : undefined,
              })
          )}
        </Wrapper>
      </Container>
    );
  }
);

Grid.displayName = 'Grid';
